import { useCallback, useMemo } from "react";
import type { Collab } from "@cabal/shared/domain/session/collab/types.js";
import type { ProductSummary } from "@cabal/shared/domain/session/product/types.js";
import type { ProductReadMarkerMap } from "@/types/frontend-types";
import {
  selectIsCollabUnread,
  selectUnreadCollabCounts,
  selectUnreadState,
} from "./read-tracking-selectors";
import type { SessionReadUnreadState } from "./use-session-read-tracking.types";

interface UseSessionReadUnreadStateInput {
  products: Record<string, ProductSummary>;
  chatMessages: Record<string, Collab>;
  chatMessageIds: readonly string[];
  collabs: Record<string, Collab>;
  collabsByProduct: Record<string, string[]>;
  ownMessageIds: ReadonlySet<string>;
  productReads: ProductReadMarkerMap;
}

export function useSessionReadUnreadState(
  input: UseSessionReadUnreadStateInput,
): SessionReadUnreadState {
  const {
    products,
    chatMessages,
    chatMessageIds,
    collabs,
    collabsByProduct,
    ownMessageIds,
    productReads,
  } = input;

  const isCollabUnread = useCallback(
    (collab: Collab, productId: string | null): boolean =>
      selectIsCollabUnread({
        collab,
        productId,
        ownMessageIds,
        productReads,
      }),
    [ownMessageIds, productReads],
  );

  const unreadCollabCounts = useMemo(
    () =>
      selectUnreadCollabCounts({
        chatMessageIds,
        chatMessages,
        collabs,
        collabsByProduct,
        ownMessageIds,
        productReads,
      }),
    [
      chatMessageIds,
      chatMessages,
      collabs,
      collabsByProduct,
      ownMessageIds,
      productReads,
    ],
  );

  const unread = useMemo(
    () => selectUnreadState({ products, collabsByProduct, unreadCollabCounts }),
    [collabsByProduct, products, unreadCollabCounts],
  );

  return {
    unreadCollabCounts,
    unread,
    isCollabUnread,
  };
}
